import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Loader2, FileText, Palette, Clock, CheckCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { unifiedAPI } from '../../utils/api';

const smallFormatTestimonials = [
  {
    id: 1,
    name: 'James R.',
    company: 'Local Business Owner',
    rating: 5,
    text: 'Outstanding quality on our business cards and brochures. The colors are vibrant and the paper stock is premium.',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&q=80'
  },
  {
    id: 2,
    name: 'Lisa T.',
    company: 'Marketing Manager',
    rating: 5,
    text: 'Fast turnaround and excellent print quality. Our flyers look amazing and really helped our campaign.',
    avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&q=80'
  },
  {
    id: 3,
    name: 'David K.',
    company: 'Event Coordinator',
    rating: 5,
    text: 'The postcards we ordered exceeded expectations. Professional quality at a great price. Highly recommend!',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&q=80'
  }
];

const SmallFormatHome = () => {
  const [categories, setCategories] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [categoriesRes, productsRes] = await Promise.all([
          unifiedAPI.getCategories({ product_type: 'SMF', include_counts: true }),
          unifiedAPI.getProducts({ product_type: 'SMF', limit: 8, sort: 'name_asc' })
        ]);
        setCategories(categoriesRes.categories || []);
        setFeaturedProducts(productsRes.products || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-ivory-50">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-24 gradient-hero">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gold-500/5 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gold-400/5 rounded-full blur-[120px]" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold-500/10 border border-gold-500/20 mb-8">
              <FileText className="w-4 h-4 text-gold-600" />
              <span className="text-gold-700 text-sm font-medium font-body">Professional Print Services</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-heading font-semibold mb-6">
              <span className="text-ivory-800">Small Format</span>
              <br />
              <span className="gradient-text-gold">Printing</span>
            </h1>
            <p className="text-xl md:text-2xl text-ivory-500 mb-10 max-w-2xl mx-auto font-body">
              Business cards, brochures, flyers, and more. Premium quality prints for every occasion.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="text-lg px-8 py-6 btn-luxury-primary rounded-lg font-body" 
                asChild
              >
                <Link to="/SmallPrint/products" data-testid="browse-small-format-btn">Browse Products</Link>
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="text-lg px-8 py-6 btn-luxury-outline rounded-lg font-body" 
                asChild
              >
                <Link to="/contact">Get Quote</Link>
              </Button>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap items-center justify-center gap-8 mt-12">
              <div className="flex items-center gap-2 text-ivory-500">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-sm font-body">Premium Paper Stocks</span>
              </div>
              <div className="flex items-center gap-2 text-ivory-500">
                <Clock className="w-5 h-5 text-gold-600" />
                <span className="text-sm font-body">Fast Turnaround</span>
              </div>
              <div className="flex items-center gap-2 text-ivory-500">
                <Palette className="w-5 h-5 text-gold-600" />
                <span className="text-sm font-body">Full Color Printing</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20 gradient-stone">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-heading font-semibold text-ivory-800 mb-4">Shop by Category</h2>
            <p className="text-xl text-ivory-500 font-body">Professional print products for your business</p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-10 h-10 text-gold-500 animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {categories.slice(0, 6).map((category) => (
                <Link
                  key={category.id}
                  to={`/SmallPrint/products/${category.id}`}
                  className="group relative aspect-[4/3] rounded-xl overflow-hidden shadow-luxury-lg"
                  data-testid={`small-format-category-${category.id}`}
                >
                  <img
                    src={category.image_url || category.image || 'https://placehold.co/400x300/f5f3f0/9a8b7d?text=Category'}
                    alt={category.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    onError={(e) => { e.target.src = 'https://placehold.co/400x300/f5f3f0/9a8b7d?text=Category'; }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ivory-900/80 via-ivory-900/40 to-transparent" />
                  <div className="absolute inset-0 p-6 flex flex-col justify-end">
                    <h3 className="text-2xl font-heading font-semibold text-white mb-2 group-hover:text-gold-300 transition-colors">
                      {category.name}
                    </h3>
                    <p className="text-ivory-200 text-sm mb-4 line-clamp-2 font-body">
                      {category.description || `Browse our ${category.name.toLowerCase()} collection`}
                    </p>
                    <div className="flex items-center text-gold-400 font-medium text-sm opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300 font-body">
                      <span>Shop Now ({category.product_count || category.count || 0} products)</span>
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 gradient-pearl">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
            <div>
              <h2 className="text-4xl md:text-5xl font-heading font-semibold text-ivory-800 mb-4">Featured Products</h2>
              <p className="text-xl text-ivory-500 font-body">Popular print solutions for your business</p>
            </div>
            <Button 
              variant="ghost" 
              className="text-gold-600 hover:text-gold-700 hover:bg-gold-500/10 mt-4 md:mt-0 font-body"
              asChild
            >
              <Link to="/SmallPrint/products">
                View All Products
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-10 h-10 text-gold-500 animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
              {featuredProducts.slice(0, 8).map((product) => (
                <Link
                  key={product.id}
                  to={`/SmallPrint/product/${product.id}`}
                  className="group"
                  data-testid={`featured-small-format-${product.id}`}
                >
                  <Card className="bg-ivory-50 border border-ivory-300/50 hover:border-gold-500/50 transition-all duration-300 overflow-hidden h-full card-luxury">
                    <div className="aspect-square overflow-hidden bg-ivory-200">
                      <img
                        src={product.image_url || product.image || 'https://placehold.co/300x300/f5f3f0/9a8b7d?text=Product'}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => { e.target.src = 'https://placehold.co/300x300/f5f3f0/9a8b7d?text=Product'; }}
                      />
                    </div>
                    <CardContent className="p-5">
                      <p className="text-xs text-gold-600 font-mono mb-1">{product.sku || product.id}</p>
                      <h3 className="font-heading font-semibold text-lg text-ivory-800 mb-2 group-hover:text-gold-700 transition-colors line-clamp-2">
                        {product.name}
                      </h3>
                      <p className="text-ivory-500 text-sm mb-3 line-clamp-2 font-body">{product.description}</p>
                      <div className="text-gold-600 font-bold text-xl font-body">
                        {product.retail_price ? (
                          <span>From ${product.retail_price.toFixed(2)}</span>
                        ) : (
                          <span>Contact for pricing</span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}

          {featuredProducts.length === 0 && !loading && (
            <div className="text-center py-12">
              <p className="text-ivory-500 font-body mb-4">No products available yet.</p>
              <Button asChild className="btn-luxury-outline">
                <Link to="/contact">Contact us for custom orders</Link>
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-ivory-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-heading font-semibold text-ivory-800 mb-4">Customer Reviews</h2>
            <p className="text-xl text-ivory-500 font-body">5.0 star rating from verified customers</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {smallFormatTestimonials.map((testimonial) => (
              <Card key={testimonial.id} className="bg-ivory-100 border border-ivory-300/50 card-luxury">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-gold-500 text-gold-500" />
                    ))}
                  </div>
                  <p className="text-ivory-600 mb-6 italic font-body">&quot;{testimonial.text}&quot;</p>
                  <div className="flex items-center gap-3">
                    <img
                      src={testimonial.avatar}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover border-2 border-gold-500/30"
                    />
                    <div>
                      <div className="font-semibold text-ivory-800 font-body">{testimonial.name}</div>
                      <div className="text-sm text-ivory-500 font-body">{testimonial.company}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-gold-700 via-gold-600 to-gold-500" />
        
        <div className="relative container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-heading font-semibold text-white mb-6">
            Ready to Print?
          </h2>
          <p className="text-xl text-gold-100 mb-10 max-w-2xl mx-auto font-body">
            Get professional quality prints for your business at competitive prices
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              size="lg" 
              className="text-lg px-8 py-6 bg-white text-gold-700 hover:bg-ivory-100 rounded-lg shadow-luxury-lg font-body font-semibold"
              asChild
            >
              <Link to="/SmallPrint/products" data-testid="cta-shop-small-format">
                Shop Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="text-lg px-8 py-6 border-2 border-white/30 bg-transparent text-white hover:bg-white/10 rounded-lg font-body"
              asChild
            >
              <Link to="/contact">
                Request Quote
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SmallFormatHome;
